/**
 * Public Project Approval Page — accessed via unique token in the URL.
 * Anyone with the link can approve/reject without logging in.
 *
 * Route: /approve-project/:token
 * Backend endpoints (public, no auth):
 *   GET  /api/projects/public-approval/:token
 *   POST /api/projects/public-approval/:token/decide
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, X, FileText, CheckCircle2, XCircle, Building2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function PublicApproval() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [decision, setDecision] = useState(null);   // "approve" | "reject"
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {status}

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BACKEND}/api/projects/public-approval/${token}`)
      .then((r) => {
        if (!alive) return;
        setState({ loading: false, error: null, data: r.data });
      })
      .catch((e) => {
        if (!alive) return;
        const detail = e?.response?.data?.detail || "Unable to load approval";
        setState({ loading: false, error: detail, data: null });
      });
    return () => { alive = false; };
  }, [token]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (decision === "reject" && !reason.trim()) { toast.error("Please add a reason for rejection"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${BACKEND}/api/projects/public-approval/${token}/decide`,
        { decision, signature_name: name.trim(), reason: reason.trim() }
      );
      setSubmitted({ status: r.data.status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-6 text-center" data-testid="public-approval-error">
          <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-2" />
          <div className="text-lg font-semibold text-slate-900">Approval link not available</div>
          <div className="text-sm text-slate-500 mt-1">{state.error}</div>
          <div className="text-xs text-slate-400 mt-3">If you already approved or rejected, no further action is needed.</div>
        </div>
      </div>
    );
  }

  if (state.data?.already_decided) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-6 text-center" data-testid="public-approval-decided">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <div className="text-lg font-semibold text-slate-900">This project has already been decided</div>
          <div className="text-sm text-slate-500 mt-1">
            {state.data.name} ({state.data.number}) — current status: <b>{state.data.status}</b>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    const approved = submitted.status === "approved";
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-6 text-center" data-testid="public-approval-success">
          {approved
            ? <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            : <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-2" />}
          <div className="text-lg font-semibold text-slate-900">
            {approved ? "Thank you — approved!" : "Rejection recorded"}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {approved
              ? "Our team has been notified and will start the project shortly."
              : "Your feedback was sent to our team. We'll get back to you with a revised proposal."}
          </div>
        </div>
      </div>
    );
  }

  const data = state.data;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Building2 className="w-6 h-6" />
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-300">Project proposal for review</div>
            <div className="text-lg font-semibold">{data.name}</div>
          </div>
          <div className="ml-auto text-xs font-mono text-slate-300">{data.number}</div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <section className="bg-white rounded-xl shadow p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Client" value={data.client_name} />
            <Field label="Property" value={data.property_name} />
            <Field label="Start date" value={data.start_date || "TBD"} />
            <Field label="Contract value" value={money(data.contract_value)} strong />
          </div>
          {data.description && (
            <div className="mt-4">
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Description</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{data.description}</div>
            </div>
          )}
        </section>

        {(data.phases || []).length > 0 && (
          <section className="bg-white rounded-xl shadow p-5">
            <div className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-2"><FileText className="w-4 h-4" /> Project scope</div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[10px] font-bold uppercase text-slate-500 border-b">
                <div className="col-span-3">Phase</div>
                <div className="col-span-6">Scopes</div>
                <div className="col-span-3">Schedule</div>
              </div>
              {data.phases.map((ph) => {
                const scopes = (data.scopes || []).filter((s) => s.phase_id === ph.id);
                return (
                  <div key={ph.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 text-xs items-start">
                    <div className="col-span-3 font-semibold text-slate-800">{ph.name}</div>
                    <div className="col-span-6 text-slate-600">
                      {scopes.length > 0
                        ? <ul className="list-disc list-inside">{scopes.map((s) => <li key={s.id}>{s.name}</li>)}</ul>
                        : <span className="text-slate-400">—</span>}
                    </div>
                    <div className="col-span-3 text-slate-500 text-[11px] tabular-nums">
                      {ph.start_date || "?"} → {ph.end_date || "?"}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {data.notes && (
          <section className="bg-white rounded-xl shadow p-5">
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Notes / conditions</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{data.notes}</div>
          </section>
        )}

        <section className="bg-white rounded-xl shadow p-5 space-y-3" data-testid="public-approval-decide">
          <div className="text-sm font-semibold text-slate-900">Your decision</div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="public-approval-approve-btn"
              onClick={() => setDecision("approve")}
              className={`flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 rounded-lg border-2 transition-all
                ${decision === "approve"
                  ? "bg-emerald-600 border-emerald-600 text-white shadow"
                  : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
            ><Check className="w-4 h-4" /> Approve project</button>
            <button
              data-testid="public-approval-reject-btn"
              onClick={() => setDecision("reject")}
              className={`flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 rounded-lg border-2 transition-all
                ${decision === "reject"
                  ? "bg-rose-600 border-rose-600 text-white shadow"
                  : "bg-white border-rose-200 text-rose-700 hover:bg-rose-50"}`}
            ><X className="w-4 h-4" /> Request changes</button>
          </div>

          {decision && (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Your full name (for the record)</label>
                <input
                  data-testid="public-approval-name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Type your name"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                />
              </div>
              {decision === "reject" && (
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">What needs to change?</label>
                  <textarea
                    data-testid="public-approval-reason"
                    value={reason} onChange={(e) => setReason(e.target.value)}
                    rows="3" placeholder="Describe what should be adjusted..."
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                  />
                </div>
              )}
              <div className="text-[10px] text-slate-500">
                By clicking Submit you confirm this decision is being made by <b>{name || "…"}</b> on behalf of {data.client_name}.
              </div>
              <button
                data-testid="public-approval-submit"
                onClick={submit} disabled={submitting}
                className={`w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 rounded-lg text-white transition-all disabled:opacity-50
                  ${decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : decision === "approve" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                {decision === "approve" ? "Confirm approval" : "Submit rejection"}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, strong }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">{label}</div>
      <div className={`${strong ? "text-lg font-bold text-slate-900 tabular-nums" : "text-sm text-slate-800"}`}>{value || "—"}</div>
    </div>
  );
}
